// src/App.js - Updated with MillMind landing page and code-splitting
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { ProtectedRoute } from './middleware/auth';
import theme from './theme';
import CurtainRaiser from './components/CurtainRaiser';

import './App.css';
import { NotificationProvider } from './pages/shared/hooks/notification';
import { UserProvider } from './pages/shared/context/userContext';

// Lazy-loaded components for code splitting (reduces initial bundle size)
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const HomePage = lazy(() => import('./pages/HomePage'));
const AdminPage = lazy(() => import('./pages/adminpage'));
const JMCDashboard = lazy(() => import('./pages/JMCDashboard'));
const DailyReportPage = lazy(() => import('./pages/DailyReportPage'));
const Consumption = lazy(() => import('./features/consuption/components/consumption'));
const RoleManagementPage = lazy(() => import('./pages/RoleManagementPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MaintenanceDashboardPage = lazy(() => import('./pages/MaintenanceDashboardPage'));
const WorkOrderPage = lazy(() => import('./pages/WorkOrderPage'));
const WorkOrderDetailPage = lazy(() => import('./pages/WorkOrderDetailPage'));
const WorkOrdersListPage = lazy(() => import('./pages/WorkOrdersListPage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const AssetDetailPage = lazy(() => import('./pages/AssetDetailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PMSchedulesPage = lazy(() => import('./pages/PMSchedulesPage'));
const FeltTrackingPage = lazy(() => import('./pages/FeltTrackingPage'));
const PaperMillDashboard = lazy(() => import('./pages/PaperMillDashboard'));
const PMCreatePage = lazy(() => import('./pages/PMCreatePage'));
const PMCalendarPage = lazy(() => import('./pages/PMCalendarPage'));
const PMGenerationPage = lazy(() => import('./pages/PMGenerationPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));

// Loading fallback component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

// Handle browser back button to prevent going back to curtain
const BackButtonHandler = () => {
  const location = useLocation();

  useEffect(() => {
    // When on landing page, replace history state to prevent back navigation issues
    if (location.pathname === '/') {
      window.history.replaceState(null, '', '/');
    }
  }, [location]);

  return null;
};

// CURTAIN RAISER: Set to true to enable, false to disable completely
const CURTAIN_ALWAYS_ON = false;

function App() {
  const [showCurtain, setShowCurtain] = useState(() => {
    // Curtain raiser is disabled - set CURTAIN_ALWAYS_ON to true to re-enable
    if (!CURTAIN_ALWAYS_ON) {
      return false;
    }

    // Check URL params for curtain control
    const urlParams = new URLSearchParams(window.location.search);

    // ?skipCurtain=true - Force skip curtain for this session
    if (urlParams.get('skipCurtain') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      return false;
    }

    // ?noCurtain=true - Permanently skip curtain (sets the flag)
    if (urlParams.get('noCurtain') === 'true') {
      localStorage.setItem('curtainShown', 'true');
      window.history.replaceState({}, '', window.location.pathname);
      return false;
    }

    // ?showCurtain=true - Force show curtain for this session (useful for demos)
    if (urlParams.get('showCurtain') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      return true;
    }

    // Show curtain on every visit when enabled
    return true;
  });

  const handleCurtainComplete = () => {
    setShowCurtain(false);
  };

  // Show curtain raiser before the main app
  if (showCurtain) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CurtainRaiser onComplete={handleCurtainComplete} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
         <UserProvider>
        <Router>
          <BackButtonHandler />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />

            {/* Protected routes */}
            <Route path="/home" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />

            {/* Legacy routes for backward compatibility */}
            <Route path="/chat" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />

            <Route path="/qa" element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            } />

            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/roles" element={
              <ProtectedRoute roles={["admin"]}>
                <RoleManagementPage />
              </ProtectedRoute>
            } />

            <Route path="/admin/settings" element={
              <ProtectedRoute roles={["admin"]}>
                <SettingsPage />
              </ProtectedRoute>
            } />

            <Route path="/jmc-dashboard"  element={

              <ProtectedRoute  roles={["admin"]} >
                <JMCDashboard />
              </ProtectedRoute>
            } />
            <Route path="/consumption" element={

              <ProtectedRoute roles={["user", "manager","admin"]} >
                <Consumption />
              </ProtectedRoute>
            } />

            <Route path="/daily-report" element={
              <ProtectedRoute>
                <DailyReportPage />
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />

            <Route path="/analytics-dashboard" element={
              <ProtectedRoute>
                <AnalyticsDashboard />
              </ProtectedRoute>
            } />
            <Route path="/maintenance-dashboard" element={
              <ProtectedRoute>
                <MaintenanceDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/work-orders" element={
              <ProtectedRoute>
                <WorkOrdersListPage />
              </ProtectedRoute>
            } />
            <Route path="/work-orders/new" element={
              <ProtectedRoute>
                <WorkOrderPage />
              </ProtectedRoute>
            } />
            <Route path="/work-orders/:id" element={
              <ProtectedRoute>
                <WorkOrderDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/felt-tracking" element={
              <ProtectedRoute>
                <FeltTrackingPage />
              </ProtectedRoute>
            } />
            <Route path="/paper-mill-dashboard" element={
              <ProtectedRoute>
                <PaperMillDashboard />
              </ProtectedRoute>
            } />
            <Route path="/approvals" element={
              <ProtectedRoute>
                <ApprovalsPage />
              </ProtectedRoute>
            } />
            <Route path="/assets" element={
              <ProtectedRoute>
                <AssetsPage />
              </ProtectedRoute>
            } />
            <Route path="/assets/:assetId" element={
              <ProtectedRoute>
                <AssetDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/pm-schedules" element={
              <ProtectedRoute>
                <PMSchedulesPage />
              </ProtectedRoute>
            } />
            <Route path="/pm-schedules/create" element={
              <ProtectedRoute>
                <PMCreatePage />
              </ProtectedRoute>
            } />
            <Route path="/pm-calendar" element={
              <ProtectedRoute>
                <PMCalendarPage />
              </ProtectedRoute>
            } />
            <Route path="/pm-generation" element={
              <ProtectedRoute roles={["admin", "planner"]}>
                <PMGenerationPage />
              </ProtectedRoute>
            } />
            {/* PM Work Orders now use unified /work-orders/:id route */}

            {/* JMC Dashboard V1 - Hidden from frontend (preparing for V2) */}
            {/* <Route path="/jmc-dashboard-v1" element={
              <ProtectedRoute>
                <JMCDashboardV1 />
              </ProtectedRoute>
            } /> */}
          </Routes>
          </Suspense>
        </Router>
        </UserProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
